import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

/**
 * Gera um ID único para item do carrinho baseado no produto e variante
 * Isso permite ter o mesmo produto com cores diferentes no carrinho
 */
const generateCartItemId = (product) => {
  const baseId = product.id;
  const variantId = product.selectedVariant?.id || '';
  return variantId ? `${baseId}-${variantId}` : baseId;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [selectedUpsells, setSelectedUpsells] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('multicell_cart');
    const savedUpsells = localStorage.getItem('multicell_upsells');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
    if (savedUpsells) {
      try {
        setSelectedUpsells(JSON.parse(savedUpsells));
      } catch (e) {
        console.error('Error loading upsells:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('multicell_cart', JSON.stringify(cart));
  }, [cart]);

  // Save upsells to localStorage
  useEffect(() => {
    localStorage.setItem('multicell_upsells', JSON.stringify(selectedUpsells));
  }, [selectedUpsells]);

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const cartItemId = generateCartItemId(product);
      const existingItem = prevCart.find(item => item.cartItemId === cartItemId);
      
      if (existingItem) {
        // Atualiza quantidade do item existente
        return prevCart.map(item =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      // Adiciona novo item com cartItemId único
      return [...prevCart, { 
        ...product, 
        cartItemId,
        quantity,
        // Garante que o preço está correto
        price: product.price || product.price_pix
      }];
    });
  };

  const removeFromCart = (cartItemId) => {
    setCart(prevCart => prevCart.filter(item => item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setSelectedUpsells([]);
  };

  // Upsell functions
  const toggleUpsell = (upsell) => {
    setSelectedUpsells(prev => {
      const exists = prev.find(u => u.id === upsell.id);
      if (exists) {
        return prev.filter(u => u.id !== upsell.id);
      }
      return [...prev, upsell];
    });
  };

  const isUpsellSelected = (upsellId) => {
    return selectedUpsells.some(u => u.id === upsellId);
  };

  const clearUpsells = () => {
    setSelectedUpsells([]);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => {
      const price = item.price || item.price_pix || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getUpsellsTotal = () => {
    return selectedUpsells.reduce((total, upsell) => total + parseFloat(upsell.price || 0), 0);
  };

  const getGrandTotal = () => {
    return getTotalPrice() + getUpsellsTotal();
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    // Upsells
    selectedUpsells,
    toggleUpsell,
    isUpsellSelected,
    clearUpsells,
    getUpsellsTotal,
    getGrandTotal
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
